import { Icon, Modal } from '@deity-io/portal-ui';
import { EntityNameDescriptionFields } from 'components/FormFields';
import type { CreateEditModelInput } from 'contracts/interfaces';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  GetPackageVersionContentInfoDocument,
  useCreateEndpointMutation,
  useUpdateEndpointMutation
} from 'services/graphql/generated/hooks';
import type { GetEndpointQuery } from 'services/graphql/generated/operations';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateEditEndpointModalProps {
  isOpen?: boolean;
  isEdit?: boolean;
  versionUuid: string;
  editEndpoint?: GetEndpointQuery;
  setOpenState: (state: boolean) => void;
  onPackageCreated: () => void;
}

export const CreateEditEndpointModal: FC<CreateEditEndpointModalProps> = ({
  isOpen = false,
  setOpenState,
  onPackageCreated,
  isEdit,
  editEndpoint,
  versionUuid
}) => {
  const refetchQueries = {
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  };
  const [create, { data: createData, loading: createLoading }] = useCreateEndpointMutation(refetchQueries);
  const [update, { data: updateData, loading: updateLoading }] = useUpdateEndpointMutation(refetchQueries);

  const CreateEndpointInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(100, 'Too long')
  });

  useEffect(() => {
    if (createData || updateData) {
      onPackageCreated();
      setOpenState(false);
    }
  }, [createData, updateData]);

  const createOrUpdate = (input: CreateEditModelInput) => {
    if (!input.description) {
      input.description = '';
    }

    return isEdit ? updateEndpoint(input) : createEndpoint(input);
  };

  const createEndpoint = (input: CreateEditModelInput) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        name: input.name,
        description: input.description,
        schema: {
          type: 'rest',
          contentType: 'application/json',
          host: '',
          path: '',
          variables: {},
          protocol: 'https',
          method: 'GET'
        }
      }
    }).then();
  };

  const formik = useFormik({
    initialValues: {
      name: editEndpoint?.getEndpoint.name ?? '',
      description: editEndpoint?.getEndpoint.description ?? ''
    },
    validateOnBlur: true,
    validationSchema: CreateEndpointInputSchema,
    onSubmit: values => createOrUpdate(values)
  });

  useEffect(() => {
    if (editEndpoint?.getEndpoint) {
      const { name, description } = editEndpoint.getEndpoint;
      formik.setValues({ name: name!, description: description! });
    }
  }, [editEndpoint?.getEndpoint]);

  const updateEndpoint = (input: CreateEditModelInput) => {
    update({
      variables: {
        packageVersionUuid: versionUuid,
        uuid: editEndpoint?.getEndpoint.uuid ?? '',
        name: input.name,
        description: input.description
      }
    }).then();
  };

  const confirmText = () => {
    if (!isEdit) {
      return `${createLoading ? 'Creating' : 'Create'} Endpoint`;
    }

    return `${updateLoading ? 'Updating' : 'Update'} Endpoint`;
  };

  if (isEdit && !editEndpoint) {
    return null!;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label={`${isEdit ? 'Edit' : 'New'} Endpoint`}
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText={confirmText()}
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <EntityNameDescriptionFields
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          entity="Endpoint"
        />
      </form>
    </Modal>
  );
};
